// ---------------------------------------------------------------------------
// TemplateForm — Metadata form (title, type, customer, disease, cadence)
// ---------------------------------------------------------------------------

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  SurveyType,
  TemplateDetail,
  CadenceUnit,
  CadenceAnchor,
} from '@/types/template';
import { useOrgNodes } from '@/hooks/use-org-nodes';

const SURVEY_TYPE_LABELS: Record<SurveyType, string> = {
  scheduled_education: 'Scheduled Education',
  biometric_triggered: 'Biometric Triggered',
  adherence: 'Adherence',
  patient_satisfaction: 'Patient Satisfaction',
};

const CADENCE_UNIT_LABELS: Record<CadenceUnit, string> = {
  day: 'Day(s)',
  week: 'Week(s)',
  month: 'Month(s)',
};

const CADENCE_ANCHOR_LABELS: Record<CadenceAnchor, string> = {
  enrollment: 'Patient enrollment date',
  case_created: 'Case created date',
  last_completion: 'Last completion of this survey',
};

const templateSchema = z
  .object({
    title: z.string().min(1, 'Title is required').max(200),
    type: z.enum(['scheduled_education', 'biometric_triggered', 'adherence', 'patient_satisfaction']),
    customerId: z.coerce.number().int().positive('Customer ID is required'),
    diseaseAssociation: z.string().optional(),
    // CMS-04 cadence — all-or-nothing.
    cadenceUnit: z.enum(['day', 'week', 'month']).optional().or(z.literal('')),
    cadenceCount: z.coerce.number().int().min(1).max(365).optional().or(z.literal('')),
    cadenceAnchor: z.enum(['enrollment', 'case_created', 'last_completion']).optional().or(z.literal('')),
  })
  .refine(
    (v) => {
      const present = [v.cadenceUnit, v.cadenceCount, v.cadenceAnchor].filter(
        (x) => x !== '' && x !== undefined,
      ).length;
      return present === 0 || present === 3;
    },
    {
      message:
        'Cadence requires all three fields (unit, count, anchor) or none at all',
      path: ['cadenceUnit'],
    },
  );

type TemplateFormValues = z.infer<typeof templateSchema>;

interface TemplateFormProps {
  template?: TemplateDetail;
  mode: 'create' | 'edit';
  onSubmit: (values: TemplateFormValues) => void;
  isPending: boolean;
  hideSubmit?: boolean;
}

export function TemplateForm({ template, mode, onSubmit, isPending, hideSubmit }: TemplateFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      title: template?.title ?? '',
      type: template?.type ?? 'scheduled_education',
      customerId: template?.customer_id ?? 1,
      diseaseAssociation: template?.disease_association ?? '',
      cadenceUnit: (template?.cadence_unit ?? '') as CadenceUnit | '',
      cadenceCount: (template?.cadence_count ?? '') as number | '',
      cadenceAnchor: (template?.cadence_anchor ?? '') as CadenceAnchor | '',
    },
  });

  const { data: orgNodes, isLoading: orgNodesLoading } = useOrgNodes();

  const selectedType = watch('type');
  const cadenceUnit = watch('cadenceUnit') as CadenceUnit | '' | undefined;
  const cadenceAnchor = watch('cadenceAnchor') as CadenceAnchor | '' | undefined;

  return (
    <form id="template-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="title" className="mb-1">Title</Label>
          <Input
            id="title"
            {...register('title')}
            placeholder="Survey template title"
          />
          {errors.title && (
            <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="type" className="mb-1">Type</Label>
          {mode === 'edit' ? (
            <Input
              id="type"
              value={SURVEY_TYPE_LABELS[template?.type ?? 'scheduled_education']}
              disabled
            />
          ) : (
            <Select
              value={watch('type')}
              onValueChange={(v) => setValue('type', v as SurveyType)}
            >
              <SelectTrigger id="type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(SURVEY_TYPE_LABELS) as [SurveyType, string][]).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        <div>
          <Label htmlFor="customerId" className="mb-1">Customer</Label>
          {mode === 'edit' && template?.status !== 'draft' ? (
            <Input
              id="customerId"
              value={orgNodes?.find((n) => n.id === String(watch('customerId')))?.name ?? String(watch('customerId'))}
              disabled
            />
          ) : orgNodesLoading ? (
            <Input id="customerId" disabled placeholder="Loading customers..." />
          ) : (
            <Select
              value={String(watch('customerId') || '')}
              onValueChange={(v) => setValue('customerId', Number(v))}
            >
              <SelectTrigger id="customerId">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                {orgNodes?.map((node) => (
                  <SelectItem key={node.id} value={node.id}>{node.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {errors.customerId && (
            <p className="mt-1 text-xs text-red-600">{errors.customerId.message}</p>
          )}
        </div>

        {(selectedType === 'biometric_triggered' || template?.type === 'biometric_triggered') && (
          <div>
            <Label htmlFor="diseaseAssociation" className="mb-1">Disease Association</Label>
            <Input
              id="diseaseAssociation"
              {...register('diseaseAssociation')}
              placeholder="e.g. CHF, COPD, Diabetes"
            />
          </div>
        )}
      </div>

      {/* CMS-04 — Cadence section */}
      <div className="rounded-ds-008 border border-silver-200 bg-silver-50/40 p-4 space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="text-sm font-semibold text-carbon-700">Cadence</h4>
          <p className="text-xs text-carbon-500">
            Optional. Configure all three fields, or leave all empty (template fires only on event triggers).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="cadenceCount" className="mb-1">Every</Label>
            <Input
              id="cadenceCount"
              type="number"
              min={1}
              max={365}
              placeholder="e.g. 90"
              {...register('cadenceCount')}
            />
          </div>

          <div>
            <Label htmlFor="cadenceUnit" className="mb-1">Unit</Label>
            <Select
              value={(cadenceUnit ?? '') as string}
              onValueChange={(v) => setValue('cadenceUnit', v === '__none__' ? '' : (v as CadenceUnit))}
            >
              <SelectTrigger id="cadenceUnit">
                <SelectValue placeholder="Select unit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">(none)</SelectItem>
                {(Object.entries(CADENCE_UNIT_LABELS) as [CadenceUnit, string][]).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="cadenceAnchor" className="mb-1">From</Label>
            <Select
              value={(cadenceAnchor ?? '') as string}
              onValueChange={(v) => setValue('cadenceAnchor', v === '__none__' ? '' : (v as CadenceAnchor))}
            >
              <SelectTrigger id="cadenceAnchor">
                <SelectValue placeholder="Select anchor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">(none)</SelectItem>
                {(Object.entries(CADENCE_ANCHOR_LABELS) as [CadenceAnchor, string][]).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {errors.cadenceUnit && (
          <p className="text-xs text-red-600">{errors.cadenceUnit.message}</p>
        )}
      </div>

      {!hideSubmit && (
        <div className="flex justify-end">
          <Button type="submit" disabled={isPending}>
            {isPending
              ? mode === 'create' ? 'Creating...' : 'Saving...'
              : mode === 'create' ? 'Create Template' : 'Save Changes'}
          </Button>
        </div>
      )}
    </form>
  );
}


